import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ApiError, request } from '../../lib/api-client.ts';
import { formatJst } from '../../lib/format.ts';
import type {
  ReportExecuteResponse,
  ReportLatestResponse,
  ReportPreviewResponse,
  ReportRecord,
  ReportType,
} from '../../types/report.ts';

type Props = {
  industryId: number;
  reportType: ReportType;
  title: string;
};

type Status =
  | { kind: 'loading' }
  | { kind: 'idle'; latest: ReportRecord | null }
  | { kind: 'preview_loading'; latest: ReportRecord | null }
  | { kind: 'preview_ready'; latest: ReportRecord | null; preview: ReportPreviewResponse }
  | { kind: 'executing'; latest: ReportRecord | null }
  | { kind: 'executed'; report: ReportExecuteResponse['report'] }
  | { kind: 'error'; message: string };

const isAbortError = (err: unknown): boolean =>
  err instanceof DOMException && err.name === 'AbortError';

/**
 * レポート/アイデア生成パネル（業種詳細から呼ばれる）。
 *
 * フロー:
 *   初回ロードで /api/analyze/report/latest?industry_id=N&type=... を取得
 *   「コスト見積」ボタン → /api/analyze/{report,ideas}?mode=preview&industry_id=N
 *   「本当に生成」ボタン → /api/analyze/{report,ideas}?mode=execute&industry_id=N
 *
 * Gemini 呼び出しは課金される操作のため、必ず preview を経由する2段階確認方式。
 */
export const ReportPanel = ({ industryId, reportType, title }: Props): React.JSX.Element => {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const mountedRef = useRef(false);
  const inFlightRef = useRef(false);
  const executeAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    const controller = new AbortController();
    request<ReportLatestResponse>(
      `/api/analyze/report/latest?industry_id=${industryId}&type=${reportType}`,
      { signal: controller.signal }
    )
      .then((data) => {
        if (!mountedRef.current) return;
        setStatus({ kind: 'idle', latest: data.report });
      })
      .catch((err) => {
        if (!mountedRef.current || isAbortError(err)) return;
        const message = err instanceof Error ? err.message : '取得に失敗';
        setStatus({ kind: 'error', message });
      });
    return () => {
      mountedRef.current = false;
      controller.abort();
      executeAbortRef.current?.abort();
    };
  }, [industryId, reportType]);

  const previewPath = reportType === 'ideas'
    ? `/api/analyze/ideas?industry_id=${industryId}&mode=preview`
    : `/api/analyze/report?industry_id=${industryId}&mode=preview`;
  const executePath = reportType === 'ideas'
    ? `/api/analyze/ideas?industry_id=${industryId}&mode=execute`
    : `/api/analyze/report?industry_id=${industryId}&mode=execute`;

  const fetchPreview = async (latest: ReportRecord | null): Promise<void> => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setStatus({ kind: 'preview_loading', latest });
    try {
      const data = await request<ReportPreviewResponse>(previewPath, { method: 'POST' });
      if (!mountedRef.current) return;
      setStatus({ kind: 'preview_ready', latest, preview: data });
    } catch (err) {
      if (!mountedRef.current) return;
      const message = err instanceof ApiError ? err.message : '見積取得に失敗';
      setStatus({ kind: 'error', message });
    } finally {
      inFlightRef.current = false;
    }
  };

  const execute = async (latest: ReportRecord | null): Promise<void> => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const controller = new AbortController();
    executeAbortRef.current = controller;
    setStatus({ kind: 'executing', latest });
    try {
      const data = await request<ReportExecuteResponse>(executePath, {
        method: 'POST',
        signal: controller.signal,
      });
      if (!mountedRef.current) return;
      setStatus({ kind: 'executed', report: data.report });
    } catch (err) {
      if (!mountedRef.current || isAbortError(err)) return;
      const message = err instanceof ApiError ? err.message : '生成に失敗';
      setStatus({ kind: 'error', message });
    } finally {
      inFlightRef.current = false;
      executeAbortRef.current = null;
    }
  };

  return (
    <section style={sectionStyle}>
      <h3 style={headStyle}>{title}</h3>
      {renderBody(status, fetchPreview, execute)}
    </section>
  );
};

const renderBody = (
  status: Status,
  fetchPreview: (latest: ReportRecord | null) => void,
  execute: (latest: ReportRecord | null) => void
): React.JSX.Element => {
  if (status.kind === 'loading') return <p style={mutedStyle}>読み込み中…</p>;
  if (status.kind === 'error') return <p style={errorStyle}>{status.message}</p>;

  if (status.kind === 'executed') {
    return (
      <>
        <p style={successStyle}>
          生成しました（{status.report.source_model} / {status.report.prompt_tokens}+{status.report.output_tokens} tok / 約{status.report.cost_yen} 円）
        </p>
        <pre style={contentStyle}>{status.report.content_md}</pre>
      </>
    );
  }

  const latest = 'latest' in status ? status.latest : null;
  return (
    <>
      {latest ? (
        <>
          <p style={metaStyle}>
            最新: {formatJst(latest.generated_at)} / model={latest.source_model ?? '—'} / 約{latest.cost_yen ?? 0} 円
          </p>
          <pre style={contentStyle}>{latest.content_md}</pre>
        </>
      ) : (
        <p style={mutedStyle}>まだ生成されていません。</p>
      )}
      <div style={actionRowStyle}>
        {status.kind === 'idle' && (
          <button type="button" onClick={() => fetchPreview(latest)} style={primaryBtnStyle}>
            コスト見積
          </button>
        )}
        {status.kind === 'preview_loading' && <span>見積中…</span>}
        {status.kind === 'preview_ready' && (
          <>
            <span style={metaStyle}>
              推定入力: {status.preview.estimated_input_tokens} tok / 約{status.preview.estimated_cost_yen} 円
            </span>
            <button type="button" onClick={() => execute(latest)} style={dangerBtnStyle}>
              本当に生成（Gemini 呼び出し）
            </button>
          </>
        )}
        {status.kind === 'executing' && <span>生成中…</span>}
      </div>
    </>
  );
};

const sectionStyle: React.CSSProperties = {
  width: '100%',
  marginBottom: 16,
  padding: 16,
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  background: '#fff',
};
const headStyle: React.CSSProperties = { margin: '0 0 12px', fontSize: 15 };
const contentStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  background: '#fafafa',
  padding: 12,
  borderRadius: 4,
  border: '1px solid #eee',
  fontSize: 13,
  fontFamily: 'inherit',
};
const actionRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  marginTop: 12,
  flexWrap: 'wrap',
};
const metaStyle: React.CSSProperties = { fontSize: 12, color: '#666' };
const mutedStyle: React.CSSProperties = { color: '#888', fontSize: 13 };
const errorStyle: React.CSSProperties = { color: '#c0392b', fontSize: 13 };
const successStyle: React.CSSProperties = { color: '#27ae60', fontSize: 13 };
const primaryBtnStyle: React.CSSProperties = { padding: '6px 12px', fontSize: 13, cursor: 'pointer' };
const dangerBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: 13,
  background: '#e74c3c',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};
